import React from 'react';
import { usePHSPACCCompetenceProfile } from '@components/phs/pacc/results/query/useCompetence';
import { useUserInfo } from '@repository/auth/useUserInfo';
import CompetencyRadar from './competencyRadar';
import CompetencyDetails from './competencyDetails';
import CompetencyInnerCard from '../common/competencyInnerCard';
import CompetencyCard from '../common/competencyCard';
import classNames from 'classnames/bind';
import styles from './competencyProfile.module.scss';

const cx = classNames.bind(styles);

const profileTooltipContent = (
  <>
    <div>
      <em>역량이란?</em>
      <p>역량은 자신이 원하는 가치를 성과를 통해서 획득하는 개인의 고유한 내재적 특성입니다.</p>
      <p>역검에서는 개인이 성과를 창출하고, 사회적으로 성장하기 위한 7가지 핵심 역량을 다음과 같이 정의하였습니다.</p>
      <ul>
        <li>긍정, 적극, 전략, 제어, 관계, 객관, 성찰</li>
      </ul>
    </div>
    <div>
      <em>역량 프로파일</em>
      <p>
        역검을 통해 파악한 응시자님의 핵심 역량과 전체 역량검사 응시자의 평균 점수가 그래프에 표현되어 있습니다.
        <br />
        응시자님은 어떤 핵심 역량이 우수한지 확인해 보세요.
      </p>
    </div>
  </>
);

const strengthTooltipContent = (
  <>
    <em>강점</em>
    <p>응시자님이 가진 핵심 역량들 중에서 가장 돋보이는 세부 역량 세 가지를 강점으로 제공합니다.</p>
    <p>어떻게 나의 강점을 어필하면 좋을지 ‘강점을 돋보여 줄 역량 면접 가이드’를 확인해보세요.</p>
  </>
);

const weaknessTooltipContent = (
  <>
    <em>약점</em>
    <p>응시자님이 가진 핵심 역량들 중에서 상대적으로 약한 세부 역량 세 가지를 약점으로 선정하여 제공합니다.</p>
    <p>어떻게 약점을 보완하면 좋을지 고민이 된다면 ‘보완할 역량 면접 가이드’를 참고해보세요.</p>
  </>
);

interface CompetencyProfileProps {
  sn: number;
}

const CompetencyProfile = ({ sn }: CompetencyProfileProps) => {
  const { data: userInfo } = useUserInfo();
  const { data: paccProfileResult } = usePHSPACCCompetenceProfile(sn);
  if (!paccProfileResult) return null;
  const userName = userInfo?.name ?? '';
  const { strengths, weaknesses } = paccProfileResult;

  return (
    <CompetencyCard title={`${userName}님의 역량 프로파일`} cardType={'gray'} iconType={'profile'}>
      <div className={cx('wrapper')}>
        {/* [D] 역량 프로파일 영역 */}
        <div className={cx('col', 'profile')}>
          <CompetencyInnerCard
            small
            title={'역량 프로파일'}
            tooltipContent={profileTooltipContent}
            subTitle={'역량에 마우스를 올려 해당 역량의 의미를 확인해보세요.'}
          >
            <CompetencyRadar paccProfileResult={paccProfileResult} />
          </CompetencyInnerCard>
        </div>

        {/* [D] 강점 약점 영역 */}
        <div className={cx('col')}>
          <CompetencyInnerCard small title={'강점'} tooltipContent={strengthTooltipContent}>
            <CompetencyDetails accProfileSubFactor={strengths} />
          </CompetencyInnerCard>

          <CompetencyInnerCard small title={'약점'} tooltipContent={weaknessTooltipContent}>
            <CompetencyDetails accProfileSubFactor={weaknesses} />
          </CompetencyInnerCard>
        </div>
      </div>
    </CompetencyCard>
  );
};

export default CompetencyProfile;
